import _extends from '@babel/runtime/helpers/extends';
import React, { useRef, useState } from 'react';
import Fuse from 'fuse.js';
import styled from 'styled-components';
import { useIsMobile, TaktIdConsumer, createStormTaktId, Flex, SearchInput } from '@amzn/storm-ui';
import { noop } from '@amzn/storm-ui-utils';

const WithHideLabel = ({
  hideSearchInputLabel,
  mobileFullScreen,
  children
}) => {
  const isMobile = useIsMobile();
  // packages/storm-ui/src/Dropdown/DropdownIsMobileContext.tsx
  const dropdownIsMobile = mobileFullScreen && isMobile || false;
  return /*#__PURE__*/React.createElement(React.Fragment, null, children({
    hideLabel: typeof hideSearchInputLabel === 'boolean' ? hideSearchInputLabel : !dropdownIsMobile,
    dropdownIsMobile
  }));
};
const FilterWrapper = /*#__PURE__*/styled(Flex).withConfig({
  componentId: "sc-storm-ui-40025937__sc-128u5fh-0"
})(["border-bottom:", ";"], ({
  theme
}) => theme.border.subtle.default);
const DEFAULT_OPTIONS = {
  shouldSort: true,
  threshold: 0.6,
  location: 0,
  distance: 100,
  maxPatternLength: 32,
  minMatchCharLength: 1,
  keys: ['label', 'value']
};
const withFilter = (WrapperComponent, noPadding) => {
  const WithFilterComponent = props => {
    const {
      searchInputId,
      clearButtonLabel = 'Clear search terms',
      onFilterChange = noop,
      onFilterClear = noop,
      onOpen = noop,
      preRender = noop,
      renderBeforeSearchInput,
      renderAfterSearchInput,
      items = [],
      options = DEFAULT_OPTIONS,
      placeholder = '',
      hideSearchInputLabel,
      searchInputLabel,
      persistFilterState = false,
      mobileFullScreen,
      ...rest
    } = props;
    const isMobile = useIsMobile();
    const filterRef = useRef(null);
    const [filterValue, setFilterValue] = useState('');
    const handleOpen = () => {
      if (!persistFilterState) {
        setFilterValue('');
        if (onFilterClear) {
          onFilterClear('');
        }
      }
      if (onOpen) {
        onOpen();
      }
    };
    const handleFilterChange = event => {
      const {
        value
      } = event.target;
      const mergedOptions = {
        ...DEFAULT_OPTIONS,
        ...options
      };
      const fuse = new Fuse(items, mergedOptions);
      // @TODO: (storm-v4) packages/storm-ui-data-visualization/src/SeriesChartControls/KpiPicker.tsx
      // This line is to make the results compatible before and after fuse.js 5.0.4
      // https://github.com/krisk/Fuse/releases/tag/v5.0.4-beta
      const results = value ? fuse.search(value).map(fuseObject => fuseObject.item ? fuseObject.item : fuseObject) : items;
      setFilterValue(value);
      if (onFilterChange) {
        onFilterChange(results, value, event);
      }
    };
    const handleFilterClear = () => {
      setFilterValue('');
      if (onFilterClear) {
        onFilterClear('');
      }
      if (filterRef) {
        var _filterRef$current;
        filterRef === null || filterRef === void 0 || (_filterRef$current = filterRef.current) === null || _filterRef$current === void 0 || _filterRef$current.focus();
      }
    };
    const handleFilterKeyDown = event => {
      const {
        key
      } = event;
      if (key === ' ' || key === 'Enter') {
        event.stopPropagation();
      }
    };
    const renderFilter = () => /*#__PURE__*/React.createElement(TaktIdConsumer, {
      fallbackId: createStormTaktId('with-filter')
    }, () => /*#__PURE__*/React.createElement(WithHideLabel, {
      hideSearchInputLabel: hideSearchInputLabel,
      mobileFullScreen: mobileFullScreen
    }, ({
      hideLabel,
      dropdownIsMobile
    }) => /*#__PURE__*/React.createElement(FilterWrapper, {
      paddingInlineStart: isMobile ? 'small' : 'base',
      paddingInlineEnd: isMobile ? 'small' : 'base',
      paddingBlockStart: "small",
      paddingBlockEnd: "small"
    }, preRender && preRender(), /*#__PURE__*/React.createElement(Flex, {
      direction: "column",
      rowGap: "xxsmall",
      fullWidth: true
    }, renderBeforeSearchInput && renderBeforeSearchInput(), /*#__PURE__*/React.createElement(SearchInput, {
      id: searchInputId,
      placeholder: placeholder,
      fullWidth: true,
      inputRef: filterRef,
      value: filterValue,
      onClear: handleFilterClear,
      onChange: handleFilterChange,
      clearButtonLabel: clearButtonLabel,
      onKeyDown: handleFilterKeyDown,
      hideLabel: hideLabel,
      label: searchInputLabel
    }), renderAfterSearchInput && renderAfterSearchInput()))));
    return /*#__PURE__*/React.createElement(WrapperComponent, _extends({}, rest, {
      mobileFullScreen: mobileFullScreen,
      preRender: renderFilter,
      onOpen: handleOpen
    }));
  };
  return WithFilterComponent;
};

export { withFilter as w };
//# sourceMappingURL=withFilter-CXO9aEFY.js.map
